import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { AppStoreButton, TireMark } from './shared/AppStoreButton';

interface LayoutProps {
  children: ReactNode;
}

// Marketing pages lay out their own full-width sections; the tool and legal
// pages get the standard content container.
const FULL_BLEED = ['/', '/how-it-works', '/tips', '/dashboard'];

const NAV = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/tips', label: 'Tire tips' },
  { to: '/inspect', label: 'Check tires online' },
  { to: '/inspections', label: 'My inspections' },
];

export const Layout = ({ children }: LayoutProps) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-line/60 no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" onClick={() => setOpen(false)}>
            <TireMark className="w-8 h-8" />
            <span className="text-[19px] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 112' }}>
              Tread AI
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn(
                    'px-3 py-2 rounded-full text-[15px] transition-colors',
                    isActive ? 'bg-brand-100 text-brand-900' : 'text-muted hover:text-ink hover:bg-surface'
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <AppStoreButton compact placement="header" />
          </div>

          <button
            type="button"
            className="md:hidden -mr-2 p-2 rounded-full hover:bg-surface"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>

        {open && (
          <nav className="md:hidden border-t border-line/60 px-4 py-3 flex flex-col gap-1" aria-label="Main">
            {NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={cn(
                  'px-3 py-3 rounded-xl text-base',
                  location.pathname === l.to ? 'bg-brand-100 text-brand-900' : 'text-ink'
                )}
              >
                {l.label}
              </Link>
            ))}
            <AppStoreButton className="mt-2 self-start" placement="mobile_menu" />
          </nav>
        )}
      </header>

      <main className="flex-1">
        {FULL_BLEED.includes(location.pathname) ? (
          children
        ) : (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">{children}</div>
        )}
      </main>

      <footer className="border-t border-line/60 bg-surface no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div className="max-w-sm">
            <Link to="/" className="flex items-center gap-2.5">
              <TireMark className="w-7 h-7" />
              <span className="font-semibold tracking-tight">Tread AI</span>
            </Link>
            <p className="mt-3 text-[15px] text-muted leading-relaxed">
              Check your tires with your phone between professional inspections. Built by a small team of software and
              machine-learning engineers in California.
            </p>
            <AppStoreButton compact className="mt-5" placement="footer" />
          </div>

          <div>
            <h2 className="text-sm font-semibold text-ink">Product</h2>
            <ul className="mt-3 space-y-2 text-[15px] text-muted">
              <li><Link className="hover:text-ink" to="/how-it-works">How it works</Link></li>
              <li><Link className="hover:text-ink" to="/tips">Tire tips</Link></li>
              <li><Link className="hover:text-ink" to="/inspect">Check tires online</Link></li>
              <li><Link className="hover:text-ink" to="/api">Developer API</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-ink">Contact</h2>
            <ul className="mt-3 space-y-2 text-[15px] text-muted">
              <li><a className="hover:text-ink" href="mailto:contact@tread-ai.com">contact@tread-ai.com</a></li>
              <li><a className="hover:text-ink" href="mailto:sizarta@gmail.com">sizarta@gmail.com</a></li>
              <li><a className="hover:text-ink tabular" href="tel:+19495584019">(949) 558-4019</a></li>
              <li><Link className="hover:text-ink" to="/terms">Terms of service</Link></li>
              <li><Link className="hover:text-ink" to="/privacy">Privacy policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 text-sm text-muted">
          © {new Date().getFullYear()} Tread AI. Readings are estimates; always have a professional confirm before replacing tires.
        </div>
      </footer>
    </div>
  );
};
